import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import OnScroll from './OnScroll';
import { HeaderContext } from './SMHeaderContext';
import { HeaderAddon } from './HeaderAddon';
import UserDropDown from './UserDropDown';
import HeaderLogo from './HeaderLogo';
import NavBarAnon from './NavBar/NavBarAnon';
import NavBarAuthed from './NavBar/NavBarAuthed';
import DrawerParent from './Drawer/DrawerParent';
import MobileDrawer from './MobileMenu/MobileDrawer';
import { MobileTabletScreen, DesktopScreen } from '../..';

import './header.scss';
import './utils.scss';

const SMHeader = ({
  isUserAuthenticated,
  showCreateSurveyButton,
  color,
  lang,
  variant,
  isEUDC,
  actionFlow,
  user,
  sticky
}) => {
  const auth = isUserAuthenticated ? 'auth' : 'anon';
  const headerColor = isUserAuthenticated ? 'green' : color;
  const headerVariant = isUserAuthenticated ? 'default' : variant;

  const navBarAnonVariant =
    !isUserAuthenticated && ['default', 'slim'].includes(variant);
  const navBarAuthVariant = isUserAuthenticated && variant === 'default';

  return (
    <HeaderContext
      showCreateSurveyButton
      isUserAuthenticated={isUserAuthenticated}
      lang={lang}
      isEUDC={isEUDC}
      actionFlow={actionFlow}
      user={user}
    >
      <>
        <OnScroll
          render={(x, y) => {
            const headerColorAnon = y > 0 ? 'white' : headerColor;
            const smHeaderColor = isUserAuthenticated
              ? headerColor
              : headerColorAnon;

            const defaultColor =
              headerColorAnon === 'transparent' ||
              headerColorAnon === 'primary';

            const logoColor = defaultColor ? 'alt' : 'primary';
            const mobileMenuColor = defaultColor ? 'alt' : 'secondary';
            const addonLogInColor = defaultColor ? 'alt' : 'secondary';
            const navBarColor = defaultColor ? 'alt' : 'secondary';
            const addonSignUpColor = defaultColor ? 'alt' : 'upgrade';
            const addonSignUpVariant = defaultColor ? 'ghost' : 'default';

            const classNames = classnames(
              `sm-header--${auth}`,
              `sm-header--${smHeaderColor}`,
              `sm-header--${headerVariant}`,
              { 'sm-header--en': lang === 'en' },
              { 'sm-header--sticky': sticky && !isUserAuthenticated }
            );

            return (
              <div className={classNames}>
                <MobileTabletScreen>
                  <MobileDrawer mobileMenuColor={mobileMenuColor} />
                </MobileTabletScreen>
                <HeaderLogo
                  variant={variant}
                  color={logoColor}
                  isUserAuthenticated={isUserAuthenticated}
                />
                {isUserAuthenticated && (
                  <div
                    style={{ order: 4 }}
                    className="sm-header--menu-drop-down--container"
                  >
                    <UserDropDown lang={lang} isEUDC={isEUDC} />
                  </div>
                )}
                <Choose>
                  <When condition={navBarAnonVariant}>
                    <NavBarAnon color={navBarColor} lang={lang} />
                  </When>
                  <When condition={navBarAuthVariant}>
                    <NavBarAuthed user={user} />
                  </When>
                </Choose>
                <HeaderAddon
                  addonSignUpColor={addonSignUpColor}
                  addonLogInColor={addonLogInColor}
                  addonSignUpVariant={addonSignUpVariant}
                  variant={variant}
                  isUserAuthenticated={isUserAuthenticated}
                  showCreateSurveyButton={showCreateSurveyButton}
                />
                {isUserAuthenticated && (
                  <DesktopScreen>
                    <DrawerParent />
                  </DesktopScreen>
                )}
              </div>
            );
          }}
        />
      </>
    </HeaderContext>
  );
};

SMHeader.propTypes = {
  sticky: PropTypes.bool,
  color: PropTypes.string,
  isUserAuthenticated: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  showCreateSurveyButton: PropTypes.bool,
  lang: PropTypes.string.isRequired,
  variant: PropTypes.string,
  isEUDC: PropTypes.bool.isRequired,
  actionFlow: PropTypes.string,
  user: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
};

SMHeader.defaultProps = {
  sticky: true,
  color: 'primary',
  isUserAuthenticated: false,
  showCreateSurveyButton: true,
  variant: 'default',
  actionFlow: 'header'
};

export default SMHeader;
